/**
 * ShopCatalog — creators/gear tile selector + filtered grid ("the big filter")
 *
 * Markup contract:
 *   [data-shop-controls]         → selector beneath the homepage wordmark
 *   [data-shop-view="<view>"]    → toggle buttons (creators | instruments —
 *                                  "instruments" is labeled "gear" in the UI)
 *   [data-shop-tiles="<view>"]   → photo tile grid for one view
 *                                  (sections/shop-catalog.liquid)
 *   [data-shop-filter="<key>"]   → gear tile; picking one filters the grid
 *   [data-shop-bar]              → active-filter bar above the grid
 *   [data-shop-catalog]          → grid section
 *   [data-shop-item]             → card wrapper carrying data-instrument and
 *                                  data-creators (space-separated handles)
 *   [data-shop-empty]            → empty-state message
 *
 * Creator tiles are plain links to collection pages — no JS. Gear tiles set a
 * client-side filter: tiles hide, the grid shows only matching cards, and the
 * active category heading remains above it. Tapping the active Gear control
 * resets the filter and returns to the category tiles. State is mirrored into
 * ?view=&filter= so old deep
 * links (/?view=instruments&filter=drums#shop) still land on a filtered grid.
 */
class ShopCatalog {
  constructor(catalog) {
    this.catalog = catalog;
    this.controls = document.querySelector('[data-shop-controls]') || catalog;
    this.grid = catalog.querySelector('[data-shop-grid]');
    this.items = [...catalog.querySelectorAll('[data-shop-item]')];
    this.empty = catalog.querySelector('[data-shop-empty]');
    this.bar = catalog.querySelector('[data-shop-bar]');
    this.barLabel = catalog.querySelector('[data-shop-bar-label]');
    // Creators is the default view — mirrors the initial markup state in
    // snippets/shop-controls.liquid (creators tab active, creators tiles shown).
    this.view = 'creators';
    this.filter = 'all';

    this.controls.addEventListener('click', this.handleClick.bind(this));
    this.catalog.addEventListener('click', this.handleClick.bind(this));
    this.applyUrlState();
  }

  handleClick(event) {
    const backBtn = event.target.closest('[data-shop-back]');
    if (backBtn && this.catalog.contains(backBtn)) {
      event.preventDefault();
      this.setFilter('all');
      this.syncUrl();
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }

    const viewBtn = event.target.closest('[data-shop-view]');
    if (viewBtn) {
      event.preventDefault();
      this.setView(viewBtn.dataset.shopView);
      this.syncUrl();
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }

    const tile = event.target.closest('[data-shop-filter]');
    if (tile && this.catalog.contains(tile)) {
      this.setFilter(tile.dataset.shopFilter);
      this.syncUrl();
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }

  applyUrlState() {
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    const filter = params.get('filter');

    if (view === 'creators' || view === 'instruments') this.setView(view);
    if (filter) this.setFilter(filter);
    if (!filter) this.render();
  }

  setView(view) {
    this.view = view;
    this.filter = 'all';

    this.controls.querySelectorAll('[data-shop-view]').forEach((btn) => {
      const active = btn.dataset.shopView === view;
      btn.classList.toggle('is-active', active);
      if (active) {
        btn.setAttribute('aria-current', 'page');
      } else {
        btn.removeAttribute('aria-current');
      }
    });

    this.render();
  }

  setFilter(filter) {
    this.filter = filter || 'all';
    this.render();
  }

  matches(item) {
    if (this.filter === 'all') return true;

    if (this.view === 'instruments') {
      return item.dataset.instrument === this.filter;
    }

    return (item.dataset.creators || '').split(/\s+/).includes(this.filter);
  }

  render() {
    const showTiles = this.filter === 'all';

    this.catalog.querySelectorAll('[data-shop-tiles]').forEach((tiles) => {
      tiles.hidden = !showTiles || tiles.dataset.shopTiles !== this.view;
    });

    if (this.bar) this.bar.hidden = showTiles;
    if (this.barLabel) this.barLabel.textContent = showTiles ? '' : this.filter;

    if (showTiles) {
      if (this.grid) this.grid.hidden = true;
      if (this.empty) this.empty.hidden = true;
      return;
    }

    let visible = 0;

    this.items.forEach((item) => {
      const show = this.matches(item);
      item.hidden = !show;
      if (show) visible += 1;
    });

    this.layout();

    if (this.grid) this.grid.hidden = false;
    if (this.empty) this.empty.hidden = visible > 0;
  }

  // Keep the same NEW-first, alphabetical order in every product view.
  layout() {
    if (!this.grid) return;
    this.items.slice().sort((a,b) =>
      Number(b.dataset.productNew || 0) - Number(a.dataset.productNew || 0) ||
      (a.dataset.productName || '').localeCompare(b.dataset.productName || '', undefined, {sensitivity:'base'})
    ).forEach(item => this.grid.appendChild(item));
  }

  syncUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set('view', this.view);

    if (this.filter === 'all') {
      url.searchParams.delete('filter');
    } else {
      url.searchParams.set('filter', this.filter);
    }

    url.hash = '';
    window.history.replaceState({}, '', url);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const catalog = document.querySelector('[data-shop-catalog]');
  if (catalog) new ShopCatalog(catalog);
});
